/**
 * SplitPaneExamples.tsx
 * Playground harness for the SplitPane shared component.
 *
 * Demonstrates drag-to-resize splits (horizontal, vertical, nested),
 * min/max constraints, percentage-based max, responsive behavior,
 * localStorage persistence, and double-click reset.
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardBody } from '@enfomentis/ui';
import { SplitPane } from '../../components/shared/SplitPane';

// =============================================================================
// Placeholder Pane Content
// =============================================================================

/**
 * Colored placeholder that fills its container and displays a label.
 */
const PanePlaceholder: React.FC<{
  label: string;
  bgClass: string;
}> = ({ label, bgClass }) => (
  <div
    className={`flex items-center justify-center h-full w-full text-sm font-medium ${bgClass}`}
  >
    {label}
  </div>
);

// =============================================================================
// Examples
// =============================================================================

export const SplitPaneExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        {/* ----------------------------------------------------------------- */}
        {/* Basic Horizontal Split */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Basic Horizontal Split
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Left/right layout with a 200px default first pane. Drag the divider
            to resize. Min: 100px, Max: 50% of container. Double-click the
            divider to reset to default.
          </p>
          <Card>
            <CardBody>
              <div className="h-64">
                <SplitPane
                  orientation="horizontal"
                  defaultSize={200}
                  minSize={100}
                  maxSize="50%"
                  firstContent={
                    <PanePlaceholder
                      label="First (left) — 200px default"
                      bgClass="bg-primary/10 text-primary"
                    />
                  }
                  secondContent={
                    <PanePlaceholder
                      label="Second (right) — flex"
                      bgClass="bg-secondary/10 text-secondary"
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Basic Vertical Split */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Basic Vertical Split
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Top/bottom layout with a 120px default first pane. Drag the divider
            to resize. Min: 60px, Max: 50% of container. Double-click the
            divider to reset to default.
          </p>
          <Card>
            <CardBody>
              <div className="h-64">
                <SplitPane
                  orientation="vertical"
                  defaultSize={120}
                  minSize={60}
                  maxSize="50%"
                  firstContent={
                    <PanePlaceholder
                      label="First (top) — 120px default"
                      bgClass="bg-success/10 text-success"
                    />
                  }
                  secondContent={
                    <PanePlaceholder
                      label="Second (bottom) — flex"
                      bgClass="bg-warning/10 text-warning"
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Constrained Split */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Constrained Split
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Horizontal split with tight constraints: minSize=100px, maxSize=40%.
            Drag the divider to verify it clamps within these bounds.
          </p>
          <Card>
            <CardBody>
              <div className="h-64">
                <SplitPane
                  orientation="horizontal"
                  defaultSize={180}
                  minSize={100}
                  maxSize="40%"
                  firstContent={
                    <PanePlaceholder
                      label="Min 100px / Max 40%"
                      bgClass="bg-danger/10 text-danger"
                    />
                  }
                  secondContent={
                    <PanePlaceholder
                      label="Second — flex"
                      bgClass="bg-secondary/10 text-secondary"
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Persisted Split */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Persisted Split
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Horizontal split with localStorage persistence via storageKey. Drag
            the divider to a new position, then reload the page — the size is
            restored from localStorage. Double-click the divider to reset and
            clear the persisted value.
          </p>
          <Card>
            <CardBody>
              <div className="h-64">
                <SplitPane
                  orientation="horizontal"
                  defaultSize={200}
                  minSize={100}
                  maxSize="60%"
                  storageKey="playground-split-persist"
                  firstContent={
                    <PanePlaceholder
                      label="Persisted — drag, then reload"
                      bgClass="bg-primary/10 text-primary"
                    />
                  }
                  secondContent={
                    <PanePlaceholder
                      label="Second — flex"
                      bgClass="bg-secondary/10 text-secondary"
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Double-Click Reset */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Double-Click Reset
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Drag the divider to resize, then double-click the divider to reset
            to the default size (200px). This split also has persistence enabled
            — double-clicking clears the persisted value too. Reload the page
            after a double-click reset to confirm the default size is restored.
          </p>
          <Card>
            <CardBody>
              <div className="h-64">
                <SplitPane
                  orientation="horizontal"
                  defaultSize={200}
                  minSize={100}
                  maxSize="60%"
                  storageKey="playground-split-reset"
                  firstContent={
                    <PanePlaceholder
                      label="Double-click divider to reset"
                      bgClass="bg-warning/10 text-warning"
                    />
                  }
                  secondContent={
                    <PanePlaceholder
                      label="Second — flex"
                      bgClass="bg-secondary/10 text-secondary"
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Nested Composition */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Nested Composition
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Horizontal split where the second (right) pane contains a vertical split —
            producing a 3-pane layout. Both levels are independently draggable and
            persisted with distinct storageKey values. Drag either divider, reload
            the page, and verify each level restores independently.
          </p>
          <Card>
            <CardBody>
              <div className="h-96">
                <SplitPane
                  orientation="horizontal"
                  defaultSize={240}
                  minSize={120}
                  maxSize="60%"
                  storageKey="playground-split-outer"
                  firstContent={
                    <PanePlaceholder
                      label="Left — 240px default"
                      bgClass="bg-primary/10 text-primary"
                    />
                  }
                  secondContent={
                    <SplitPane
                      orientation="vertical"
                      defaultSize={160}
                      minSize={80}
                      maxSize="70%"
                      storageKey="playground-split-inner"
                      firstContent={
                        <PanePlaceholder
                          label="Top-right — 160px default"
                          bgClass="bg-success/10 text-success"
                        />
                      }
                      secondContent={
                        <PanePlaceholder
                          label="Bottom-right — flex"
                          bgClass="bg-warning/10 text-warning"
                        />
                      }
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Responsive Behavior */}
        {/* ----------------------------------------------------------------- */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            Responsive Behavior
          </h3>
          <p className="text-sm text-text-secondary mb-4">
            Dividers are hidden on viewports below 768px. Resize your browser
            window to verify: on mobile widths, panes render at their default
            proportions with no divider or resizing. On desktop widths (768px+),
            the interactive divider reappears and the last-dragged position is
            restored.
          </p>
          <Card>
            <CardBody>
              <div className="h-48">
                <SplitPane
                  orientation="horizontal"
                  defaultSize={200}
                  minSize={80}
                  maxSize="60%"
                  firstContent={
                    <PanePlaceholder
                      label="Resize browser to test"
                      bgClass="bg-primary/10 text-primary"
                    />
                  }
                  secondContent={
                    <PanePlaceholder
                      label="Responsive demo"
                      bgClass="bg-secondary/10 text-secondary"
                    />
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};
